import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import viewZones from '@/shared/api/shipping/zones/viewZones';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';
import Zone from '@/shared/types/shipping/zones/zone.type';

const tableColumnsList = [
  'delivery_city',
  'delivery_country',
  'delivery_state',
  'exclusionary',
  'in_account_id',
  'in_tenant_id',
  'shipping_profile_id',
  'shipping_profile_zone_id',
  'shipping_provider_key',
  'shipping_rate',
  'shipping_rate_type',
  'ts_created',
  'ts_modified',
];

const tableRequiredColumns = [
  'delivery_country',
  'delivery_state',
  'delivery_city',
  'exclusionary',
  'shipping_rate_type',
  'shipping_rate',
  'actions',
];

type Args = {
  shippingProfile: ShippingProfile;
  refreshTableIndex?: number;
};

const useManageZonesTable_v2 = ({
  shippingProfile,
  refreshTableIndex,
}: Args) => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();
  const requestBodyRef = useRef<typeof requestBody | null>(null);

  const [table, setTable] = useState({
    data: [] as Zone[],
    totalRows: 0,
    isLoading: false,
  });

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: tableColumnsList,
      tableRequiredColumns: tableRequiredColumns,
    });

  useEffect(() => {
    const fetchZones = async () => {
      setTable((prev) => ({ ...prev, isLoading: true }));

      const response = await viewZones({
        body: {
          ...requestBody,
          search: [
            {
              shipping_profile_id: {
                cop: ['='],
                value: shippingProfile.shipping_profile_id,
                op: 'and',
              },
            },
            requestBody.search.length === 0 ? null : requestBody.search,
          ],
        },
        onUnauthorized: redirectLogin,
      });

      setTable((prev) => ({ ...prev, isLoading: false }));

      if (response.success) {
        setTable((prev) => ({
          ...prev,
          data: response.data,
          totalRows: response.total_records || 0,
        }));
      }
    };

    if (
      !isEqual(requestBody, requestBodyRef.current) ||
      refreshTableIndex ||
      shippingProfile.shipping_profile_id
    ) {
      requestBodyRef.current = requestBody;

      void fetchZones();
    }
  }, [
    refreshTableIndex,
    shippingProfile.shipping_profile_id,
    requestBody,
    redirectLogin,
  ]);

  return {
    apiRef,
    model,
    table,
    columns,
    visibilityModel,
    setVisibilityModel,
  };
};

export default useManageZonesTable_v2;
